<template>
  <div>
    <div class="headline5 mb-4">
      {{ $t(`modals.addGlobalMspPolicy.policies.emailSecurity.title`) }}
    </div>
    <v-tabs
      v-model="selectedTab"
      align-with-title
      color="orange-base"
      background-color="white"
      slider-color="orange-base"
      :height="48"
    >
      <v-tab
        v-for="tab in tabs"
        :key="tab.id"
        :data-testid="`email-security-tab-${tab.id}`"
        class="mr-8"
        :height="42"
      >
        {{ tab.label }}
      </v-tab>
    </v-tabs>
    <v-window v-model="selectedTab">
      <v-window-item v-for="tab in tabs" :key="tab.id">
        <div v-if="selectedTab === 0" class="mt-2">
          <real-time-security-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            :action-not-allowed="disabled"
            data-testid="email-security-real-time-settings"
          />
          <sensitivity-level-selector
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            class="mt-8"
            data-testid="email-security-sensitivity-selector"
          />
          <quarantine-file-types-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            class="mt-8"
            data-testid="email-security-quarantine-file-types"
          />
          <div v-if="localValue.exclusionSecuritySettingsDto" class="mt-8 subtitle1">
            {{ $t("emailSettings.settingsTab.scanBypass") }}
          </div>
          <security-awareness-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            class="mt-4"
            data-testid="email-security-awareness-settings"
          />
          <subject-line-exclusion-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            class="mt-4"
            data-testid="email-security-subject-line-exclusion"
          />
          <div class="mt-8 subtitle1">
            {{ $t("emailSettings.settingsTab.apiBasedEmailSecurity") }}
          </div>
          <quarantine-folder-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            :action-not-allowed="false"
            data-testid="email-security-quarantine-folder"
          />
          <point-of-contact-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled"
            class="mt-8"
          />

          <block-auto-forwarding-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled || !localValue.adminEmail"
            :is-global-policy="true"
            class="mt-8"
          />
          <blocked-email-reports-settings
            v-model="localValue"
            :disabled="!localValue.securityEnabled || disabled || !localValue.adminEmail"
            :is-global-policy="true"
            class="mt-8"
          />
        </div>
        <template v-else>
          <v-card class="mt-4">
            <v-card-text>
              <secure-message-settings
                v-model="localValue"
                :disabled="false"
                data-testid="email-security-secure-message"
              />
            </v-card-text>
          </v-card>
        </template>
      </v-window-item>
    </v-window>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, computed } from "vue";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";
import cloneDeep from "lodash/cloneDeep";
import type { GlobalPolicyConfig } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { useI18n } from "vue-i18n";
import SecurityAwarenessSettings from "@/components/settings/email-settings/SecurityAwarenessSettings.vue";
import SubjectLineExclusionSettings from "@/components/settings/email-settings/SubjectLineExclusionSettings.vue";
import RealTimeSecuritySettings from "@/components/settings/email-settings/RealTimeSecuritySettings.vue";
import QuarantineFolderSettings from "@/components/settings/email-settings/QuarantineFolderSettings.vue";
import QuarantineFileTypesSettings from "@/components/settings/email-settings/QuarantineFileTypesSettings.vue";
import SensitivityLevelSelector from "@/components/settings/email-settings/SensitivityLevelSelector.vue";
import SecureMessageSettings from "@/components/settings/email-settings/SecureMessageSettings.vue";
import BlockAutoForwardingSettings from "@/components/settings/email-settings/BlockAutoForwardingSettings.vue";
import BlockedEmailReportsSettings from "@/components/settings/email-settings/BlockedEmailReportsSettings.vue";
import PointOfContactSettings from "@/components/settings/email-settings/PointOfContactSettings.vue";

export default defineComponent({
  components: {
    PointOfContactSettings,
    BlockedEmailReportsSettings,
    BlockAutoForwardingSettings,
    SecureMessageSettings,
    SensitivityLevelSelector,
    QuarantineFileTypesSettings,
    QuarantineFolderSettings,
    RealTimeSecuritySettings,
    SubjectLineExclusionSettings,
    SecurityAwarenessSettings,
  },
  props: {
    data: {
      type: Object as PropType<GlobalPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:data"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const localValue = ref(
      cloneDeep(
        props.data[GlobalPolicySection.EMAIL_SECURITY] ??
          defaultEmptyPolicies[GlobalPolicySection.EMAIL_SECURITY]
      )
    );
    const selectedTab = ref(0);
    const tabs = computed(() => [
      { id: "settings", label: i18n.t("emailSettings.tabs.settings") },
      { id: "coro-mail", label: i18n.t("emailSettings.tabs.coroMail") },
    ]);

    watch(
      localValue,
      (newVal) => {
        emit("update:data", {
          ...props.data,
          [GlobalPolicySection.EMAIL_SECURITY]: newVal,
        });
      },
      { immediate: true, deep: true }
    );

    return {
      localValue,
      selectedTab,
      tabs,
    };
  },
});
</script>

<style scoped lang="scss"></style>
